import { ArrowBack } from '@mui/icons-material';
import DoneAllSharpIcon from '@mui/icons-material/DoneAllSharp';
import EditIcon from '@mui/icons-material/Edit';
import {
  Card,
  Grid,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Chip,
  ListItemButton,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { VendorDTO, getVendorById, updateVendorStatusById } from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  WsColors,
  WsIconMoreVer,
  WsIconText,
  ScrollableButtonTabs,
  getDateUsingFormat,
} from 'wisestep-base';

import PopoverOnClick from '../../../features/clientListingPage/components/PopoverOnClick';

import VendorBasicInfo from './VendorBasicInfoTab';
import VendorContactsList from './VendorContactsList';
import VendorDocumentsList from './VendorDocumentsList';

const VendorDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [vendor, setVendor] = useState<VendorDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [parentTabIndex, setParentTabIndex] = useState<number>(0);

  const fetchVendorDetails = useCallback(async () => {
    try {
      if (id) {
        const response = await getVendorById(id);
        setVendor(response.data);
      }
    } catch (error) {
      console.error('Error fetching vendor details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVendorDetails();
  }, [fetchVendorDetails]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (!vendor) {
    return (
      <Typography variant="h6" align="center">
        Vendor not found
      </Typography>
    );
  }

  const handleVendorContactStatus = async (id: string, markStatus: boolean) => {
    await updateVendorStatusById(id, markStatus);
    fetchVendorDetails();
  };

  return (
    <>
      <Box borderRadius={2}>
        <Card sx={{ borderRadius: 4, px: 1, py: 2, backgroundColor: WsColors.TheBlues900 }}>
          <Grid container alignItems="center" spacing={1} justifyContent="space-between">
            <Grid item>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <IconButton sx={{ color: WsColors.White }}>
                    <ArrowBack onClick={() => navigate('/vendorsList')} />
                  </IconButton>
                </Grid>
                <Grid item>
                  <WsTypography
                    key="2"
                    variant={WsTypoVariants.TITLE}
                    color={WsTypoColors.WHITE}
                    cursor="pointer"
                  >
                    {vendor?.name}
                  </WsTypography>
                </Grid>
                <Grid item>
                  <Chip
                    label={vendor?.active ? 'Active' : 'Inactive'}
                    color="primary"
                    sx={{
                      color: WsColors.White,
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      fontSize: 14,
                    }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <PopoverOnClick element={<WsIconMoreVer />}>
                <Grid
                  p={1}
                  sx={{
                    backgroundColor: WsColors.TheBlues900,
                  }}
                >
                  <ListItemButton>
                    <WsIconText
                      text="Edit Vendor"
                      textCursor="pointer"
                      icon={<EditIcon sx={{ height: 23, width: 23, color: WsColors.White }} />}
                      textColor={WsTypoColors.PRIMARY}
                      onClick={() => navigate(`/vendorslist/edit/${vendor.id}`)}
                    />
                  </ListItemButton>
                  <ListItemButton>
                    <WsIconText
                      text={`Mark as ${vendor?.active ? 'Inactive' : 'Active'}`}
                      textCursor="pointer"
                      icon={
                        <DoneAllSharpIcon
                          sx={{ height: '23', width: '23', color: WsColors.White }}
                        />
                      }
                      textColor={WsTypoColors.PRIMARY}
                      onClick={() =>
                        handleVendorContactStatus(
                          vendor?.id ?? '',
                          vendor?.active == true ? false : true
                        )
                      }
                    />
                  </ListItemButton>
                </Grid>
              </PopoverOnClick>
            </Grid>
          </Grid>
          <Grid ml={6} spacing={2}>
            <WsTypography
              variant={WsTypoVariants.BODY2}
              color={WsTypoColors.WHITE}
              cursor="pointer"
            >
              {`Created on ${
                vendor.createdOn ? getDateUsingFormat(vendor.createdOn.toString(), 'DD-MMM-YY') : ''
              }`}
            </WsTypography>
          </Grid>
        </Card>
        <Grid item borderRadius={2} sx={{ background: WsColors.White }} mt={2} mb={1}>
          <ScrollableButtonTabs
            scrollList={[
              { tabName: 'Vendor Information' },
              { tabName: 'Documents' },
              { tabName: 'Contacts' },
            ]}
            getTabIndex={(index: number) => setParentTabIndex(index)}
            defaultValue={parentTabIndex}
          />
        </Grid>
        {parentTabIndex === 0 && <VendorBasicInfo vendor={vendor} />}
        {parentTabIndex === 1 && vendor.id && <VendorDocumentsList vendorId={vendor.id} />}
        {parentTabIndex === 2 && vendor.id && <VendorContactsList vendorId={vendor.id} />}
      </Box>
    </>
  );
};

export default VendorDetails;
